import { useAPI } from '../hooks/useAPI';
import type { SupplierHealthRollup } from '../types';
import { MetricCard } from './shared/MetricCard';
import { formatNumber, formatPercent, statusBadgeClass, titleCase } from '../utils/format';

interface SupplierHealthProps {
  tenantId: number | null;
}

export function SupplierHealth({ tenantId }: SupplierHealthProps) {
  const path = tenantId ? `/api/suppliers/health?tenant_id=${tenantId}` : '';
  const { data, loading, error } = useAPI<SupplierHealthRollup>(path);

  if (!tenantId) {
    return (
      <div className="card max-w-2xl">
        <h2 className="text-lg font-semibold text-slate-900">Choose an organization first</h2>
        <p className="mt-2 text-sm text-slate-500">
          Supplier Health is tenant-scoped. Complete onboarding for an organization to view its normalized supplier model.
        </p>
      </div>
    );
  }

  if (loading) return <p className="text-slate-500">Loading supplier health…</p>;
  if (error) return <p className="text-rose-600">Failed to load: {error}</p>;
  if (!data) return null;

  const { totals, at_risk, needs_data, by_category } = data;
  const measuredPct = totals.total > 0 ? totals.measured / totals.total : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Total Suppliers" value={formatNumber(totals.total)} />
        <MetricCard label="Active" value={formatNumber(totals.active)} tone="good" />
        <MetricCard
          label="Avg Reliability"
          value={totals.avg_reliability === null ? '—' : totals.avg_reliability.toFixed(1)}
          tone={totals.avg_reliability === null ? undefined : totals.avg_reliability >= 80 ? 'good' : 'warn'}
        />
        <MetricCard
          label="Performance Coverage"
          value={formatPercent(measuredPct)}
          tone={measuredPct >= 0.8 ? 'good' : 'warn'}
        />
      </div>

      {totals.measured === 0 && totals.total > 0 && (
        <section className="rounded-xl border border-sky-200 bg-sky-50 p-4">
          <p className="font-medium text-sky-900">Supplier master data is live. Performance is not measured yet.</p>
          <p className="mt-1 text-sm text-sky-700">
            Connect PO receipts, ASN, or delivery history next to calculate reliability and on-time performance.
            Jupiter leaves these metrics unknown rather than treating missing history as zero performance.
          </p>
        </section>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">At-Risk Suppliers</h2>
          {at_risk.length === 0 ? (
            <p className="text-sm text-slate-400">No measured supplier risk detected.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {at_risk.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.name}</p>
                    <p className="text-xs text-slate-400">{titleCase(s.category)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-slate-500">
                      {s.reliability_score === null ? '—' : s.reliability_score.toFixed(0)}
                    </span>
                    <span className={`badge ${statusBadgeClass(s.status)}`}>{s.status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="card">
          <h2 className="mb-3 text-sm font-semibold text-slate-700">Needs Performance Data</h2>
          {needs_data.length === 0 ? (
            <p className="text-sm text-slate-400">All suppliers have performance history.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {needs_data.map((s) => (
                <li key={s.id} className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{s.name}</p>
                    <p className="text-xs text-slate-400">{titleCase(s.category)}</p>
                  </div>
                  <span className="badge bg-slate-100 text-slate-600">Not measured</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="card">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Reliability by Category</h2>
        <ul className="space-y-3">
          {by_category.map((c) => (
            <li key={c.category}>
              <div className="mb-1 flex justify-between text-sm">
                <span className="font-medium text-slate-700">{titleCase(c.category)}</span>
                <span className="text-slate-500">
                  {c.avg_reliability === null ? 'Not measured' : c.avg_reliability.toFixed(1)} · {c.measured_count}/{c.count} measured
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                {c.avg_reliability !== null && (
                  <div
                    className="h-full rounded-full bg-brand-500"
                    style={{ width: `${Math.min(100, c.avg_reliability)}%` }}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
